import { isPlatformBrowser } from '@angular/common';
import { Injectable, PLATFORM_ID, inject } from '@angular/core';
import { PreloadingStrategy, Route } from '@angular/router';
import { Observable, of } from 'rxjs';
import { mergeMap } from 'rxjs/operators';

/**
 * Preloads lazy route chunks in the background once the browser goes idle, so
 * the first navigation to a route is instant instead of paying the chunk
 * download on click. Deferring to idle keeps preloading off the critical path,
 * so it never competes with the initial render (LCP/TTI) or SSR hydration.
 *
 * Opt a route out with `data: { preload: false }` — used for rarely-hit routes
 * (admin, maintenance, terms) that aren't worth the bandwidth for every visitor.
 */
@Injectable({ providedIn: 'root' })
export class IdlePreloadStrategy implements PreloadingStrategy {
  private readonly platformId = inject(PLATFORM_ID);

  preload(route: Route, load: () => Observable<unknown>): Observable<unknown> {
    // Preloading is a browser-only, post-bootstrap concern — never on the server.
    if (!isPlatformBrowser(this.platformId)) return of(null);
    // Explicit opt-out for rarely-navigated routes.
    if (route.data?.['preload'] === false) return of(null);
    return whenIdle().pipe(mergeMap(() => load()));
  }
}

type IdleWindow = Window & {
  requestIdleCallback?: (cb: () => void, opts?: { timeout: number }) => number;
  cancelIdleCallback?: (handle: number) => void;
};

/**
 * Emits once when the browser is idle. Uses requestIdleCallback where available
 * (Chrome/Firefox), falling back to a short timeout (Safari). The 2s timeout on
 * rIC guarantees the chunk eventually preloads even on a busy main thread.
 */
function whenIdle(): Observable<void> {
  return new Observable<void>((subscriber) => {
    const w = window as IdleWindow;
    if (typeof w.requestIdleCallback === 'function') {
      const handle = w.requestIdleCallback(
        () => { subscriber.next(); subscriber.complete(); },
        { timeout: 2000 },
      );
      return () => w.cancelIdleCallback?.(handle);
    }
    const id = setTimeout(() => { subscriber.next(); subscriber.complete(); }, 2000);
    return () => clearTimeout(id);
  });
}
